import { router } from '@inertiajs/react'
import { FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { Button } from '@atoms'
import AppErrorsAlert from '@molecules/Alerts/AppErrorsAlert'
import * as FormContainer from '@molecules/FormContainer'
import BackLink from '@molecules/BackLink'

import { serverErrorsToFormErrors } from '@frontend/adapters/serverErrorsAdapter'
import type { GroupEditQueryType } from '@frontend/graphql/queries/groups'

import { useErrorsStore } from '@stores/errorsStore'

import FormFullPageLayout from '@layouts/FormFullPageLayout'

import useFormWithErrors from '@hooks/useFormWithErrors'
import useMutation from '@hooks/useGQLMutation'

import { scopedTranslation } from '@utils/I18n'

import GroupFormPage, { type ValidationSchema, validationSchema } from '../features/edit-group-details/GroupFormPage'
import { UpdateGroupDocument, type UpdateGroupMutation } from '@frontend/graphql/mutations/updateGroup.generated'
import GroupDetailsAdapter from '../adapters/GroupDetailsAdapter'

const t = scopedTranslation('group_edit')
const tShared = scopedTranslation('shared')

type GroupEditProps = {
  group: GroupEditQueryType['group']
  primarySiteBaseUrl: string
}

export default function GroupEdit({ group, primarySiteBaseUrl }: GroupEditProps) {
  const { setErrors: setAppErrors, clearErrors: clearAppErrors } = useErrorsStore(({ setErrors, clearErrors }) => ({
    setErrors,
    clearErrors,
  }))
  const { setErrors: setFormErrors, ...formMethods } = useFormWithErrors({
    defaultValues: {
      group: GroupDetailsAdapter.fromServer(group).toFrontend(),
    },
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  })

  const { mutate } = useMutation(UpdateGroupDocument)

  const onSubmit = async (values: ValidationSchema) => {
    clearAppErrors('editGroup')
    mutate(GroupDetailsAdapter.fromFrontend({ id: group.id, ...values.group }).toServer(), {
      onSuccess: (response: UpdateGroupMutation) => {
        if (!response.updateGroup) return

        if (response.updateGroup.success && response.updateGroup.group) {
          router.visit(`/groups/${response.updateGroup.group.id}`)
        } else {
          setFormErrors({
            errorsBasePath: 'group',
            errors: response.updateGroup.errors,
            adapter: serverErrorsToFormErrors,
          })
          setAppErrors({
            errorsPath: 'editGroup',
            errors: response.updateGroup.errors,
          })
        }
      },
      onError: () => {
        setAppErrors({
          errorsPath: 'editGroup',
          errors: [
            {
              message: '',
              code: 'unknown',
            },
          ],
        })
      },
    })
  }

  return (
    <FormFullPageLayout>
      <div className="tw:flex tw:flex-col tw:gap-4 tw:py-16 tw:min-h-screen tw:mx-auto tw:max-w-7xl tw:font-sans">
        <AppErrorsAlert errorsPath="editGroup" />
        <FormProvider {...formMethods}>
          <FormContainer.Root>
            <FormContainer.FormCard dataCompId={'edit-group_form'}>
              <FormContainer.FormCardHeader title={t('group_details.header', { name: group.name })} />
              <FormContainer.Form>
                <GroupFormPage primarySiteBaseUrl={primarySiteBaseUrl} />
              </FormContainer.Form>
            </FormContainer.FormCard>
          </FormContainer.Root>
        </FormProvider>

        <div className="tw:flex tw:justify-center tw:gap-2">
          <BackLink to={`/groups/${group.id}`} rank="secondary" color="danger">
            {tShared('buttons.cancel')}
          </BackLink>
          <Button
            dataCompId={'group-edit_form-submit'}
            type="submit"
            size="md"
            trailingIcon="save-disk"
            onClick={formMethods.handleSubmit(onSubmit)}
          >
            {t('group_details.submit')}
          </Button>
        </div>
      </div>
    </FormFullPageLayout>
  )
}
